import { CardType } from '../../../../models/card-model';
import { ChartType } from '../../../../models/chart-config.model';
import { CHART_TYPE_REQUIREMENTS } from '../../../../services/chart-config-change.types';

/** Line and area charts use a date axis plus an optional breakdown dimension. */
export function isTimeSeriesChartType(chartType: string): boolean {
	const reqs = CHART_TYPE_REQUIREMENTS[chartType];
	return reqs?.timeSeries === true;
}

/** Bar, column, and donut use a category dimension on the horizontal axis. */
export function usesCategoryAxisChartType(chartType: string): boolean {
	const reqs = CHART_TYPE_REQUIREMENTS[chartType];
	return reqs?.usesCategory === true;
}

/** Scatter chart type (fixed horizontal / vertical metric fields on the Data tab). */
export function isScatterChartType(chartType: string): boolean {
	return chartType === ChartType.Scatter || chartType === 'scatter';
}

/** Table cards expose time dimension + grouping dimension in separate sections. */
export function isTableCardType(cardType: CardType): boolean {
	return cardType === CardType.Table;
}

export type ChartDataTabSectionIcon =
	| 'dimension'
	| 'metric'
	| 'metric-horizontal'
	| 'metric-vertical';

const CHART_DATA_TAB_SECTION_ICON_FILES: Record<
	string,
	Partial<Record<ChartDataTabSectionIcon, string>> &
		Record<'dimension' | 'metric', string>
> = {
	line: {
		dimension: 'line-chart-dimension.svg',
		metric: 'line-chart-metric.svg',
	},
	area: {
		dimension: 'area-chart-dimension.svg',
		metric: 'area-chart-metric.svg',
	},
	bar: {
		dimension: 'bar-chart-dimension.svg',
		metric: 'bar-chart-metric.svg',
	},
	column: {
		dimension: 'column-chart-dimension.svg',
		metric: 'column-chart-metric.svg',
	},
	donut: {
		dimension: 'donut-chart-dimension.svg',
		metric: 'donut-chart-metric.svg',
	},
	scatter: {
		dimension: 'scatter-chart-dimension.svg',
		metric: 'scatter-chart-metric-horizontal.svg',
		'metric-horizontal': 'scatter-chart-metric-horizontal.svg',
		'metric-vertical': 'scatter-chart-metric-vertical.svg',
	},
	number: {
		dimension: 'line-chart-dimension.svg',
		metric: 'number-chart-metric.svg',
	},
};

const DEFAULT_CHART_DATA_TAB_SECTION_ICON_FILES =
	CHART_DATA_TAB_SECTION_ICON_FILES.line;

/** Reporting chart card Data tab section heading icon (not table cards). */
export function chartDataTabSectionIconSrc(
	chartType: string,
	section: ChartDataTabSectionIcon
): string {
	const normalized = chartType?.trim().toLowerCase() || 'line';
	const files =
		CHART_DATA_TAB_SECTION_ICON_FILES[normalized] ??
		DEFAULT_CHART_DATA_TAB_SECTION_ICON_FILES;
	const file = files[section] ?? files.metric;
	return `assets/icons/${file}`;
}

/** Schema field suitable for `timeDimension.dimension` (date / time axis). */
export function isSchemaTimeDimensionField(field: {
	name: string;
	dataType?: string;
}): boolean {
	const dataType = field.dataType?.toLowerCase();
	if (dataType === 'time' || dataType === 'date') {
		return true;
	}
	const name = field.name.toLowerCase();
	return name === 'date' || name.endsWith('.date');
}

/**
 * Human-readable label for a time-dimension technical id before analytics schema loads.
 * Prefer schema `label` once `availableDimensions` is populated.
 */
export function timeDimensionDisplayLabelFallback(
	technicalName: string
): string {
	const trimmed = technicalName?.trim() ?? '';
	if (!trimmed) {
		return '';
	}
	const lastDot = trimmed.lastIndexOf('.');
	const segment = lastDot >= 0 ? trimmed.slice(lastDot + 1) : trimmed;
	const normalized = segment.replace(/[_-]+/g, ' ').trim();
	if (!normalized) {
		return trimmed;
	}
	if (normalized.toLowerCase() === 'date') {
		return 'Date';
	}
	return normalized.replace(/\b\w/g, c => c.toUpperCase());
}
